import { UserStatsDto, bsConstants, newDate, validStr } from 'booksprout'
import { AuthenticatedUser } from '../modules/_base/auth/auth.service.interface'

/**
 * The decisions the app shell makes on every visitor, once a minute.
 *
 * The root component of all four apps polls the session, keeps the unread counts and
 * user stats fresh, decides whether to hand this visitor to HelpHero, and prompts for a reload when the
 * server has been deployed underneath them. What lives here is the judgement in all of that — who gets
 * a product tour, whether it is worth polling at all, which errors are worth escalating — with no Vue,
 * no services and no `$q` in sight.
 *
 * The polling itself lives in `useAppRuntime`, which is where this file's docblock said it would go once
 * the four `App.ts` files converted. `BaseApp` is gone with them.
 */

export function useAppShell () {
  /**
   * Whether this visitor should be handed to HelpHero for a product tour.
   *
   * Three things have to hold: they still have something to be shown (or the hard cut-off date has not
   * passed), they have not already completed every tour, and they are an author on the author app —
   * reviewers and admins are never toured.
   *
   * Every `=== 0` and `=== false` here is "this visitor has not done that yet", so any single unfinished
   * step is enough to keep offering help.
   */
  const shouldSendHelpHeroData = (
    stats: UserStatsDto,
    user: AuthenticatedUser,
    isReviewerApp: boolean
  ): boolean => {
    const hasSomethingLeftToShow =
      // forced on until this date, whatever the stats say
      bsConstants.APP.HELP_HERO_STOP_HELP >= newDate() ||
      stats.arcCount === 0 ||
      stats.penNameCount === 0 ||
      stats.bookCount === 0 ||
      stats.arcTeamCount === 0 ||
      stats.reviewsCount === 0 ||
      stats.completeArcCount === 0 ||
      stats.newReviewCount === 0 ||
      stats.pendingTeamMembers === 0 ||
      stats.anyArcHasParticipant === false ||
      stats.anyPenNameHasFollower === false ||
      stats.anyTeamHasMember === false ||
      stats.hasHadTeamMemberForOneMonth === false ||
      stats.hasReviewOlderThanOneMonth === false

    const completedAllTours = bsConstants.APP.HELP_HERE_COMPLETE_ON_TOURS.sort().join(',') ===
      user.completedTours?.split(',').sort().join(',')

    return hasSomethingLeftToShow &&
      !completedAllTours &&
      validStr(user.authorRole) &&
      isReviewerApp === false
  }

  /**
   * HelpHero only runs against production, and never for an admin — an admin mimicking an author would
   * otherwise pick up that author's tour progress.
   */
  const isHelpHeroEnabled = (user: AuthenticatedUser | undefined) => {
    return process.env.BUILD_MODE === 'master' && !!user && !user.isAdmin
  }

  /**
   * A completed tour is appended to the visitor's list, without duplicating one already there.
   */
  const completedToursWith = (existing: string | undefined, tourName: string): string => {
    const tours = existing ? existing.split(',') : []

    if (tours.indexOf(tourName) === -1) {
      tours.push(tourName)
    }

    return tours.join(',')
  }

  /**
   * The stats go to HelpHero as visitor properties, and apollo's `__typename` is not one of them.
   */
  const statsForHelpHero = (stats: UserStatsDto) => {
    const { __typename, ...rest } = stats as UserStatsDto & { __typename?: string }
    return rest
  }

  /**
   * The interval is disabled under test: one call at mount is enough, and a hanging api request in any
   * test would otherwise fail an unrelated one.
   */
  const shouldRunIntervalRoutines = () => {
    return ![process.env.ENVIRONMENT, process.env.BUILD_MODE].includes('testing')
  }

  const isVersionMismatch = (serverVersion: string | undefined) => {
    return serverVersion !== bsConstants.APP.VERSION
  }

  /**
   * A timed-out liveness check is not worth reporting: nothing can be done about it and Sentry does not
   * need it.
   */
  const isTimeoutError = (e: { code?: string } | undefined) => e?.code === 'ECONNABORTED'

  /**
   * Builds Quasar's icon resolver over the app's sprite. Returning `void 0` for an unknown name lets
   * Quasar fall through to its own icon sets.
   */
  const iconMapFn = (icons: Record<string, string>, spritePath: string) => {
    return (iconName: string) => {
      const icon = icons[iconName]

      return icon !== void 0 ? { icon: spritePath + icon } : void 0
    }
  }

  return {
    shouldSendHelpHeroData,
    isHelpHeroEnabled,
    completedToursWith,
    statsForHelpHero,
    shouldRunIntervalRoutines,
    isVersionMismatch,
    isTimeoutError,
    iconMapFn
  }
}
